import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadState } from "../../constants/enums";
import { initialAiChatState, IChatMessage, IAiChatState, ISession } from "./aiChat.types";
import { fetchChatHistory, sendChatMessage, fetchSessions } from "./aiChatThunk";
import { APIError } from "../../constants/types";

const aiChatSlice = createSlice({
  name: "aiChat",
  initialState: initialAiChatState,
  reducers: {
    // ─── Chat Window ───────────────────────────────────────────────────────────
    toggleChat: (state: IAiChatState) => {
      state.isOpen = !state.isOpen;
    },
    setChatOpen: (state: IAiChatState, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    clearMessages: (state: IAiChatState) => {
      state.messages = [];
      state.history = initialAiChatState.history;
    },

    // ─── Streaming (via Socket) ────────────────────────────────────────────────
    addUserMessage: (state: IAiChatState, action: PayloadAction<string>) => {
      state.messages.push({
        id: Date.now().toString(),
        role: "user",
        content: action.payload,
        timestamp: Date.now(),
      });
    },
    startAIMessage: (state: IAiChatState, action: PayloadAction<{ id: string }>) => {
      state.messages.push({
        id: action.payload.id,
        role: "ai",
        content: "",
        timestamp: Date.now(),
        isStreaming: true,
      });
      state.loading = true;
    },
    appendToAIMessage: (
      state: IAiChatState,
      action: PayloadAction<{ id: string; delta: string }>,
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message && message.role === "ai") {
        message.content += action.payload.delta;
      }
    },
    completeAIMessage: (
      state: IAiChatState,
      action: PayloadAction<{ id: string; final_output: string }>,
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message && message.role === "ai") {
        message.content = action.payload.final_output;
        message.isStreaming = false;
        message.error = false;
      }
      state.loading = false;
    },
    setAIMessageError: (
      state: IAiChatState,
      action: PayloadAction<{ id: string; message?: string }>,
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message && message.role === "ai") {
        if (action.payload.message) {
          message.content = action.payload.message;
        }
        message.error = true;
        message.isStreaming = false;
      }
      state.loading = false;
    },
    setLoading: (state: IAiChatState, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    addAiMessage: (state: IAiChatState, action: PayloadAction<string>) => {
      state.messages.push({
        id: Date.now().toString(),
        role: "ai",
        content: action.payload,
        timestamp: Date.now(),
      });
    },
    // ─── File Context ─────────────────────────────────────────────────────────
    setFileContext: (state: IAiChatState, action: PayloadAction<string>) => {
      state.fileContext = action.payload;
      state.isOpen = true;
    },
    clearFileContext: (state: IAiChatState) => {
      state.fileContext = null;
    },

    // ─── Session Management ────────────────────────────────────────────────────
    setActiveSession: (state: IAiChatState, action: PayloadAction<string | null>) => {
      state.activeSessionId = action.payload;
      state.messages = [];
    },
    clearMessagesForNewChat: (state: IAiChatState) => {
      state.activeSessionId = null;
      state.messages = [];
      state.loading = false;
      state.history = {
        loading: LoadState.NotLoaded,
        error: null,
        page: 1,
        hasMore: true,
      };
    },
    prependSession: (state: IAiChatState, action: PayloadAction<ISession>) => {
      state.sessions.list.unshift(action.payload);
    },
    updateSessionTitle: (
      state: IAiChatState,
      action: PayloadAction<{ sessionId: string; title: string }>,
    ) => {
      const session = state.sessions.list.find(
        (s) => s.session_id === action.payload.sessionId,
      );
      if (session) {
        session.title = action.payload.title;
      }
    },
  },

  extraReducers: (builder) => {
    // ─── fetchChatHistory ──────────────────────────────────────────────────────
    builder
      .addCase(fetchChatHistory.pending, (state: IAiChatState) => {
        state.history.loading = LoadState.InProgress;
        state.history.error = null;
      })
      .addCase(fetchChatHistory.fulfilled, (state: IAiChatState, action: PayloadAction<{ messages: IChatMessage[]; hasMore: boolean; page: number }>) => {
        const { messages, hasMore, page } = action.payload;
        state.history.hasMore = hasMore;
        state.history.page = page;
        state.history.loading = LoadState.Loaded;

        if (page === 1) {
          // Initial load: replace all messages
          state.messages = messages;
        } else {
          // Pagination: prepend older messages without touching current chat
          state.messages = [...messages, ...state.messages];
        }
      })
      .addCase(fetchChatHistory.rejected, (state: IAiChatState, action: PayloadAction<APIError | undefined>) => {
        state.history.loading = LoadState.Failed;
        state.history.error =
          action.payload?.message ?? "Failed to load history.";
      });

    // ─── sendChatMessage ───────────────────────────────────────────────────────
    builder
      .addCase(sendChatMessage.pending, (state: IAiChatState) => {
        state.loading = true;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        const sessionId = action.payload.session_id;
        // Always store the session_id from the response
        if (sessionId) {
          if (state.activeSessionId === null) {
            // New chat: store session and prepend to list
            const now = new Date().toISOString();
            state.sessions.list.unshift({
              session_id: sessionId,
              title: action.meta.arg.message.slice(0, 50),
              created_at: now,
              updated_at: now,
            });
            // Mark history as loaded so the useEffect doesn't refetch and overwrite
            // the current messages that are already in the UI from streaming
            state.history.loading = LoadState.Loaded;
            state.history.hasMore = false;
          }
          state.activeSessionId = sessionId;
        }
      })
      .addCase(sendChatMessage.rejected, (state: IAiChatState, action: PayloadAction<(APIError & { pendingId?: string }) | undefined>) => {
        const pendingId = action.payload?.pendingId;
        
        let targetMsg = null;
        if (pendingId) {
          targetMsg = state.messages.find(m => m.id === pendingId);
        } else {
          // Fallback: Mark the last AI placeholder as an error
          targetMsg = [...state.messages]
            .reverse()
            .find((m) => m.role === "ai" && m.isStreaming);
        }

        if (targetMsg) {
          targetMsg.error = true;
          targetMsg.isStreaming = false;
          targetMsg.content =
            action.payload?.message ?? "Request failed. Please try again.";
        }
        state.loading = false;
        // Clean up global pending ID on error
        if ((window as any).__pendingAiMessageId__ === pendingId) {
          (window as any).__pendingAiMessageId__ = null;
        }
      });

    // ─── fetchSessions ─────────────────────────────────────────────────────────
    builder
      .addCase(fetchSessions.pending, (state: IAiChatState) => {
        state.sessions.loading = LoadState.InProgress;
        state.sessions.error = null;
      })
      .addCase(fetchSessions.fulfilled, (state: IAiChatState, action: PayloadAction<{ sessions: ISession[]; totalRecords: number; page: number }>) => {
        const { sessions, totalRecords, page } = action.payload;
        state.sessions.page = page;
        state.sessions.totalRecords = totalRecords;
        state.sessions.loading = LoadState.Loaded;

        if (page === 1) {
          // First page: replace the list
          state.sessions.list = sessions;
        } else {
          // Subsequent pages: append to existing list
          state.sessions.list = [...state.sessions.list, ...sessions];
        }

        // Determine if there are more sessions to load
        state.sessions.hasMore = totalRecords > state.sessions.list.length;
      })
      .addCase(fetchSessions.rejected, (state: IAiChatState, action: PayloadAction<APIError | undefined>) => {
        state.sessions.loading = LoadState.Failed;
        state.sessions.error =
          action.payload?.message ?? "Failed to load sessions.";
      });
  },
});

export const {
  toggleChat,
  setChatOpen,
  clearMessages,
  addUserMessage,
  startAIMessage,
  appendToAIMessage,
  completeAIMessage,
  setAIMessageError,
  setLoading,
  addAiMessage,
  setFileContext,
  clearFileContext,
  setActiveSession,
  clearMessagesForNewChat,
  prependSession,
  updateSessionTitle,
} = aiChatSlice.actions;

export const aiChatReducer = aiChatSlice.reducer;
